import {
  Children,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router';

import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import Text from '@cartwhl/ui/base/Text';

import './HeaderSubMenu.css';

interface HeaderSubMenuProps {
  trigger: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}

export default function HeaderSubMenu({
  trigger,
  children,
  className = '',
  contentClassName = '',
}: HeaderSubMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const contentClasses = [
    'header-sub-menu-content shadow-menu dark:bg-gray-75 min-w-max rounded-2xl bg-white',
    contentClassName || 'p-1.5',
  ].join(' ');

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(true);
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Small delay to allow moving to dropdown content
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen]);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="cursor-pointer">{trigger}</div>

      {isVisible && (
        <div
          className={`absolute -left-4 top-full z-20 pt-4 ${className}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className={contentClasses}
            data-state={isOpen ? 'open' : 'closed'}
            onAnimationEnd={handleAnimationEnd}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

interface HeaderSubMenuItemProps {
  title: string;
  subtitle?: string;
  href: string;
  comingSoon?: boolean;
  comingSoonText?: string;
  onComingSoonClick?: (slug?: string) => void;
  external?: boolean;
}

HeaderSubMenu.Item = function HeaderSubMenuItem({
  title,
  subtitle,
  href,
  comingSoon = false,
  comingSoonText = 'Coming soon',
  onComingSoonClick,
  external = false,
}: HeaderSubMenuItemProps) {
  const isOnHomepage = getHomepageUrl('/') === `${window.location.origin}/`;
  const className =
    'flex cursor-pointer flex-col rounded-lg px-4 py-[11px] transition-colors hover:bg-gray-15 dark:hover:bg-gray-90';

  // Extract slug from href (e.g., /product/cartographer -> cartographer)
  const slug = href.split('/').pop();

  if (comingSoon) {
    return (
      <div className={className} onClick={() => onComingSoonClick?.(slug)}>
        <Text variant="studio/body/medium/sm">
          {title} - <i>{comingSoonText}</i>
        </Text>
        {subtitle && (
          <Text variant="studio/body/medium/sm" className="text-tertiary">
            {subtitle}
          </Text>
        )}
      </div>
    );
  }

  if (external) {
    return (
      <a
        href={href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Text variant="studio/body/medium/sm">{title}</Text>
        {subtitle && (
          <Text variant="studio/body/medium/sm" className="text-tertiary">
            {subtitle}
          </Text>
        )}
      </a>
    );
  }

  if (isOnHomepage) {
    return (
      <Link to={href} className={className}>
        <Text variant="studio/body/medium/sm">{title}</Text>
        {subtitle && (
          <Text variant="studio/body/medium/sm" className="text-tertiary">
            {subtitle}
          </Text>
        )}
      </Link>
    );
  }

  return (
    <a href={getHomepageUrl(href)} className={className}>
      <Text variant="studio/body/medium/sm">{title}</Text>
      {subtitle && (
        <Text variant="studio/body/medium/sm" className="text-tertiary">
          {subtitle}
        </Text>
      )}
    </a>
  );
};

interface HeaderSubMenuSectionGridProps {
  children: ReactNode;
}

HeaderSubMenu.SectionGrid = function HeaderSubMenuSectionGrid({
  children,
}: HeaderSubMenuSectionGridProps) {
  return <div className="flex items-start">{children}</div>;
};

interface HeaderSubMenuSectionProps {
  title: string;
  children: ReactNode;
}

HeaderSubMenu.Section = function HeaderSubMenuSection({
  title,
  children,
}: HeaderSubMenuSectionProps) {
  return (
    <div className="flex w-[201px] min-w-[201px] flex-col py-2">
      <div className="px-2">
        <div className="border-primary border-b px-4 py-[11px]">
          <Text variant="studio/body/medium/sm" className="text-tertiary">
            {title}
          </Text>
        </div>
      </div>
      <div className="px-2">{children}</div>
    </div>
  );
};

interface HeaderSubMenuGridProps {
  children: ReactNode;
  columns?: number | number[];
}

HeaderSubMenu.Grid = function HeaderSubMenuGrid({
  children,
  columns = 3,
}: HeaderSubMenuGridProps) {
  const items = Children.toArray(children);
  const childCount = items.length;
  const columnSizes = Array.isArray(columns)
    ? columns.map((size) => Math.max(0, Math.floor(size)))
    : Array.from({ length: Math.max(1, Math.floor(columns)) }, (_, index) => {
        const columnCount = Math.max(1, Math.floor(columns));
        const baseItemsPerColumn = Math.floor(childCount / columnCount);
        const extraItems = childCount % columnCount;
        return baseItemsPerColumn + (index < extraItems ? 1 : 0);
      });

  const columnChunks: ReactNode[][] = [];
  let startIndex = 0;
  for (const size of columnSizes) {
    columnChunks.push(items.slice(startIndex, startIndex + size));
    startIndex += size;
  }

  // Keep any overflow children visible if explicit column sizes under-allocate.
  if (startIndex < childCount) {
    const overflow = items.slice(startIndex);
    if (columnChunks.length === 0) {
      columnChunks.push(overflow);
    } else {
      columnChunks[columnChunks.length - 1] = [
        ...columnChunks[columnChunks.length - 1],
        ...overflow,
      ];
    }
  }

  return (
    <div className="flex">
      {columnChunks.map((columnItems, index) => (
        <div key={index} className="flex w-[200px] min-w-[200px] flex-col">
          {columnItems}
        </div>
      ))}
    </div>
  );
};
